/**
 * Pure chart helpers for the Usage Report pie chart.
 *
 * `UsageReportViewer` feeds the raw `getUsageReport` entries through
 * `bucketSlices` (top-N + "Other" readability bucketing) before handing them
 * to Recharts, and uses `sliceShare` for the per-slice percentage shown in the
 * legend/tooltip. Both functions are pure (no React, no I/O) so the pie-chart
 * math is testable in isolation.
 *
 * See design "Components and Interfaces > 5. Usage_Report_Viewer — Pure chart
 * helpers (`src/audit/usageChart.ts`)".
 *
 * Requirements: 2.1, 2.2, 2.4, 2.5
 */

/**
 * One entry of the Usage_Report_API result: a Dimension_Value label and its
 * Operation_Count (matches the `UsageReportEntry` GraphQL wire shape).
 */
export type UsageEntry = { dimension_value: string; operation_count: number };

/**
 * One pie slice: its display label (the Dimension_Value, or "Other"), its
 * count, and whether it is the combined "Other" bucket (Req 2.2).
 */
export type Slice = { label: string; count: number; isOther: boolean };

/**
 * Maximum number of individual slices displayed before the remaining entries
 * collapse into a single "Other" slice (Req 2.2 cutoff).
 */
export const MAX_SLICES = 10;

/** Label of the bucket slice that combines the remaining entries (Req 2.2). */
const OTHER_LABEL = "Other";

/**
 * Order entries by Operation_Count descending; entries with equal counts are
 * ordered by case-insensitive alphabetical order of their labels (Req 2.2).
 */
function compareEntries(a: UsageEntry, b: UsageEntry): number {
  if (a.operation_count !== b.operation_count) {
    return b.operation_count - a.operation_count;
  }
  const aLabel = a.dimension_value.toLowerCase();
  const bLabel = b.dimension_value.toLowerCase();
  if (aLabel < bLabel) return -1;
  if (aLabel > bLabel) return 1;
  return 0;
}

/**
 * Bucket usage entries into pie slices.
 *
 * Entries are sorted by Operation_Count descending with ties broken by
 * case-insensitive label ascending (Req 2.2). When there are at most
 * `maxSlices` entries, every entry becomes its own slice and no "Other" slice
 * is produced (Req 2.1). When there are more, the first `maxSlices` entries
 * become individual slices and all remaining entries collapse into exactly one
 * additional "Other" slice (`isOther: true`) whose count is the sum of the
 * remaining entries' counts (Req 2.2). In both cases the slice counts sum
 * exactly to the total of the input counts (Req 2.5).
 */
export function bucketSlices(
  entries: UsageEntry[],
  maxSlices: number = MAX_SLICES
): Slice[] {
  const sorted = [...entries].sort(compareEntries);
  const toSlice = (entry: UsageEntry): Slice => ({
    label: entry.dimension_value,
    count: entry.operation_count,
    isOther: false,
  });
  if (sorted.length <= maxSlices) {
    return sorted.map(toSlice);
  }
  const otherCount = sorted
    .slice(maxSlices)
    .reduce((sum, entry) => sum + entry.operation_count, 0);
  return [
    ...sorted.slice(0, maxSlices).map(toSlice),
    { label: OTHER_LABEL, count: otherCount, isOther: true },
  ];
}

/**
 * Percentage share of `count` in `total`, rounded to exactly one decimal place
 * (e.g. 12.3 for 12.34...%) (Req 2.4).
 *
 * `total` is expected to be positive: the viewer renders a chart (and hence
 * computes shares) only when at least one entry with a positive count exists —
 * the zero-total case renders the empty state instead (Req 2.6).
 */
export function sliceShare(count: number, total: number): number {
  const percentage = (count / total) * 100;
  return Math.round(percentage * 10) / 10;
}
